import React, { useMemo, useState } from 'react';

function openPopup(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

function buildShareLink(baseLink, channel) {
  if (!baseLink) {
    return '';
  }

  const url = new URL(baseLink, window.location.origin);
  if (channel) {
    url.searchParams.set('via', channel);
  }
  return url.toString();
}

function StatTile({ label, value, tone = 'default' }) {
  return (
    <div className={`pfp-referral-card__stat pfp-referral-card__stat--${tone}`}>
      <div className="pfp-referral-card__stat-value">{value}</div>
      <div className="pfp-referral-card__stat-label">{label}</div>
    </div>
  );
}

export default function ReferralCard({
  user,
  referralData,
  stats,
  loading,
  error,
  onTrackShare,
  onReload,
}) {
  const [copyState, setCopyState] = useState('idle');
  const [busyChannel, setBusyChannel] = useState('');

  const safeCode = String(referralData?.code || '').trim();
  const fallbackBase = `${window.location.origin}${window.location.pathname}`;
  const safeLink = String(referralData?.link || (safeCode ? `${fallbackBase}?ref=${encodeURIComponent(safeCode)}` : '')).trim();

  const sharePayload = useMemo(() => {
    const fallbackText = safeLink
      ? `Ahoi! Sammel mit mir Pfand bei Pfandpirat und starte direkt deinen ersten Pfandeintrag: ${safeLink}`
      : '';

    return {
      emailLink: buildShareLink(safeLink, 'email'),
      whatsappLink: buildShareLink(safeLink, 'whatsapp'),
      copyLink: buildShareLink(safeLink, 'copy'),
      baseText: String(referralData?.shareText || fallbackText).trim(),
    };
  }, [referralData?.shareText, safeLink]);

  const handleShare = async (channel) => {
    if (!safeLink) {
      return;
    }

    setBusyChannel(channel);
    try {
      await Promise.resolve(onTrackShare?.(channel));

      if (channel === 'whatsapp') {
        const text = `${sharePayload.baseText.replace(safeLink, sharePayload.whatsappLink)}`;
        openPopup(`https://wa.me/?text=${encodeURIComponent(text)}`);
        return;
      }

      if (channel === 'email') {
        const subject = `Ahoi von ${user?.name || 'Pfandpirat'}`;
        const body = `${sharePayload.baseText.replace(safeLink, sharePayload.emailLink)}`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      }
    } finally {
      setBusyChannel('');
    }
  };

  const handleCopy = async () => {
    if (!sharePayload.copyLink) {
      return;
    }

    setCopyState('copying');

    try {
      await Promise.resolve(onTrackShare?.('copy'));
      await navigator.clipboard.writeText(sharePayload.copyLink);
      setCopyState('done');
      window.setTimeout(() => setCopyState('idle'), 2200);
    } catch (err) {
      console.error('Referral link copy failed', err);
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 2400);
    }
  };

  return (
    <section className="pfp-referral-card">
      <div className="pfp-referral-card__header">
        <div>
          <div className="pfp-referral-card__eyebrow">Freunde einladen</div>
          <h3 className="pfp-referral-card__title">Dein persönlicher Piraten-Link</h3>
          <p className="pfp-referral-card__intro">
            Teile deinen Link per WhatsApp, E-Mail oder direkt zum Kopieren. Die XP-Belohnung wird erst fällig,
            wenn sich ein neuer Nutzer registriert und seinen ersten Pfandeintrag anlegt.
          </p>
        </div>

        <div className="pfp-referral-card__badge">
          <span className="pfp-referral-card__badge-label">Dein Code</span>
          <strong>{loading ? '…' : safeCode || 'Noch kein Code'}</strong>
        </div>
      </div>

      <div className="pfp-referral-card__link-box">
        <label className="pfp-referral-card__label">Einladungslink</label>
        <div className="pfp-referral-card__link-row">
          <input
            type="text"
            className="pfp-referral-card__input"
            value={safeLink}
            readOnly
            placeholder={loading ? 'Einladungslink wird geladen …' : 'Kein Einladungslink verfügbar'}
          />
          <button type="button" className="pfp-referral-card__copy-button" onClick={handleCopy} disabled={loading || !safeLink}>
            {copyState === 'copying' ? 'Kopiere …' : copyState === 'done' ? 'Kopiert' : copyState === 'error' ? 'Fehler' : 'Link kopieren'}
          </button>
        </div>
      </div>

      <div className="pfp-referral-card__actions">
        <button
          type="button"
          className="pfp-referral-card__action pfp-referral-card__action--whatsapp"
          onClick={() => handleShare('whatsapp')}
          disabled={loading || !safeLink || busyChannel !== ''}
        >
          {busyChannel === 'whatsapp' ? 'Öffne WhatsApp …' : 'Bei WhatsApp teilen'}
        </button>

        <button
          type="button"
          className="pfp-referral-card__action pfp-referral-card__action--email"
          onClick={() => handleShare('email')}
          disabled={loading || !safeLink || busyChannel !== ''}
        >
          {busyChannel === 'email' ? 'Öffne E-Mail …' : 'Per E-Mail teilen'}
        </button>
      </div>

      {error ? (
        <div className="pfp-referral-card__error">
          Referral-Daten konnten gerade nicht geladen werden.
          <button type="button" className="pfp-referral-card__retry" onClick={() => onReload?.()}>
            Erneut laden
          </button>
        </div>
      ) : null}

      <div className="pfp-referral-card__stats-grid">
        <StatTile label="Geteilt" value={stats?.shares_total ?? 0} />
        <StatTile label="Link-Klicks" value={stats?.clicks_total ?? 0} />
        <StatTile label="Registriert" value={stats?.registered_total ?? 0} />
        <StatTile label="Belohnt" value={stats?.rewarded_total ?? 0} tone="success" />
        <StatTile label="Verdiente XP" value={stats?.earned_xp_total ?? 0} tone="xp" />
      </div>
    </section>
  );
}
